// src/utils/markerNames.js
//
// One display-name map for lab markers. Backend milestone/notification text
// arrives raw and often lowercase ("ldl cholesterol - direct & triglycerides
// flagged"); anything shown to a person goes through formatMarkerText so the
// same marker reads the same on Today, Chapters, the chart and the bell.
//
// Add new markers here — keys are lowercase, longest match wins.

const MARKER_NAMES = {
  'ldl cholesterol - direct': 'LDL cholesterol (direct)',
  'ldl cholesterol': 'LDL cholesterol',
  'hdl cholesterol': 'HDL cholesterol',
  'total cholesterol': 'Total cholesterol',
  'triglycerides': 'Triglycerides',
  'tg/hdl ratio': 'TG/HDL ratio',
  'vldl': 'VLDL',
  'hba1c': 'HbA1c',
  'homa-ir': 'HOMA-IR',
  'alt': 'ALT',
  'ast': 'AST',
  'alp': 'ALP',
  'ggt': 'GGT',
  'tsh': 'TSH',
  'crp': 'CRP',
  'hs-crp': 'hs-CRP',
  'egfr': 'eGFR',
  'vitamin d': 'Vitamin D',
  'vitamin b12': 'Vitamin B12',
  'hemoglobin': 'Hemoglobin',
  'fasting glucose': 'Fasting glucose',
  'fasting insulin': 'Fasting insulin',
  // Short forms the backend emits as-is ("ldl flagged", "sgpt +6 markers flagged").
  'ldl': 'LDL',
  'hdl': 'HDL',
  'tg': 'TG',
  'sgpt': 'SGPT',
  'sgot': 'SGOT',
  't3': 'T3',
  't4': 'T4',
  'ft3': 'FT3',
  'ft4': 'FT4',
  'psa': 'PSA',
  'esr': 'ESR',
  'rbc': 'RBC',
  'wbc': 'WBC',
  'mcv': 'MCV',
  'mch': 'MCH',
  'mchc': 'MCHC',
  'rdw': 'RDW',
  'bun': 'BUN',
  'ldh': 'LDH',
  'inr': 'INR',
  'apob': 'ApoB',
};

const escapeRe = s => s.replace(/[.*+?^${}()|[\]\\/]/g, '\\$&');

// Longest names first so "ldl cholesterol - direct" wins over "ldl cholesterol".
const NAME_RE = new RegExp(
  `(?<![\\w])(${Object.keys(MARKER_NAMES)
    .sort((a, b) => b.length - a.length)
    .map(escapeRe).join('|')})(?![\\w])`,
  'gi',
);

/** Display name for a single marker; falls back to sentence case. */
export function formatMarkerName(name) {
  if (!name) return '';
  const known = MARKER_NAMES[String(name).trim().toLowerCase()];
  return known || sentenceCase(name);
}

/**
 * Free text that may embed marker names ("ldl cholesterol - direct &
 * triglycerides flagged") → known markers normalised, first letter capitalised.
 * Text that is already mixed-case is left alone apart from marker names.
 */
export function formatMarkerText(text) {
  if (!text) return text;
  // Mid-sentence, plain words ("Triglycerides") drop back to lowercase; names
  // with their own capitals (LDL, HbA1c, Vitamin D) keep the canonical form.
  const replaced = String(text).replace(NAME_RE, (m, _g, offset) => {
    const name = MARKER_NAMES[m.toLowerCase()];
    return offset > 0 && !/[A-Z]/.test(name.slice(1)) ? name.charAt(0).toLowerCase() + name.slice(1) : name;
  });
  return /[A-Z]/.test(replaced) ? replaced : sentenceCase(replaced);
}

function sentenceCase(s) {
  const t = String(s).trim();
  return t ? t.charAt(0).toUpperCase() + t.slice(1) : t;
}
